import { useEffect, useState } from "react";
import { BrandLogo } from "@/components/brand/BrandLogo";
import { Link } from "react-router-dom";
import { toast } from "react-hot-toast";
import { useProgram } from "@/contexts/program";
import { Search } from "react-feather";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/empty-state";
import { usePortalScope } from "@/contexts/portalScope";

export function ActiveBrands() {
  const { activePrograms:programs, fetchPrograms } = useProgram();
  const { isMarketplaceHidden } = usePortalScope();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchActivePrograms = async () => {
      try {
        setIsLoading(true);
          await fetchPrograms();
      } catch (err) {
        console.error("Failed to fetch active programs:", err);
        setError("Failed to load active programs. Please try again later.");
        toast.error("Failed to load active programs");
      } finally {
        setIsLoading(false);
      }
    };

    fetchActivePrograms();
  }, []); // Only fetch on mount

  if (isLoading) {
    return (
      <main>
        <div className="mb-6">
          <div className="h-8 w-48 bg-muted rounded animate-pulse" />
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <div
              key={i}
              className="bg-card rounded-xl p-6 shadow-sm animate-pulse"
            >
              <div className="aspect-square flex items-center justify-center">
                <div className="w-[120px] h-[120px] bg-muted rounded" />
              </div>
            </div>
          ))}
        </div>
      </main>
    );
  }

  if (error) {
    return (
      <main>
        <div className="mb-6">
          <h1 className="text-2xl font-semibold tracking-tight">View Active Brands</h1>
        </div>
        <div className="bg-red-50 text-red-700 p-4 rounded-lg">{error}</div>
      </main>
    );
  }

  if (!isLoading && programs.length === 0) {
    return (
      <main>
        <div className="mb-6">
          <h1 className="text-2xl font-semibold tracking-tight">View Active Brands</h1>
        </div>

        <EmptyState
          icon={<Search />}
          title="No active brand partnerships yet"
          body={
            isMarketplaceHidden
              ? "You'll see your brand here once you're added to a program"
              : "Discover and join brand programs that match your niche"
          }
          action={
            isMarketplaceHidden ? undefined : (
              <Button asChild>
                <Link to="/affiliate/brands/search">Discover Brands</Link>
              </Button>
            )
          }
        />
      </main>
    );
  }

  return (
    <main>
        {/* Header */}
        <div className="mb-6 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
          <h1 className="text-2xl font-semibold tracking-tight">View Active Brands</h1>
          {!isMarketplaceHidden && (
            <Button asChild>
              <Link to="/affiliate/brands/search">Find More Brands</Link>
            </Button>
          )}
        </div>

        {/* Brands Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {programs.map((program) => (
            <Link
              key={program.id}
              to={`/affiliate/brands/${program.id}`}
              className="bg-card rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow"
            >
              <div className="aspect-square flex items-center justify-center">
                <BrandLogo
                  src={program.brandProfileImage}
                  name={program.companyName}
                  className="w-[120px] h-[120px] rounded-lg text-3xl"
                  imgClassName="object-contain"
                />
              </div>
              <div className="mt-4 text-center">
                <h3 className="font-medium text-foreground break-words overflow-hidden text-ellipsis">
                  {program.companyName}
                </h3>
                <p className="text-sm text-muted-foreground mt-1">
                  {program.program.commissionValue}% Commission
                </p>
              </div>
            </Link>
          ))}
        </div>
    </main>
  );
}
